/**
 * xOVR Engine
 *
 * Blends a coach's scouting OVR with observed game stats using an additive delta model:
 *
 *   xOVR = scoutingOVR + (rawStatDelta × confidenceWeight)
 *
 * OVR lives on a 1–100 scale (calculateOVR converts 1–5 attribute ratings → 1–100).
 * All deltas in this file are expressed in those same 1–100 units.
 *
 * The scouting OVR is always the anchor. Stats shift it, never replace it.
 * Max possible swing: ±10 points at full confidence. A 77 scout can become
 * a 87 xOVR after a dominant season, or a 67 after a rough one.
 *
 * Confidence scales with sample size (PA or IP). Small samples produce small
 * shifts; a full season produces the full delta. This prevents a 3-PA hot
 * streak from inflating a player's profile.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PlayerStats {
  // Batting — counting + rates
  gp?: number;
  pa?: number;
  ab?: number;
  avg?: number;
  obp?: number;
  ops?: number;
  slg?: number;
  h?: number;
  bb?: number;
  so?: number;       // strikeouts as batter
  rbi?: number;
  r?: number;
  xbh?: number;      // extra base hits
  sb?: number;
  sbPct?: number;
  hbp?: number;
  // Batting — story stats (GC export, added 2026-07; absent on older imports)
  qab?: number;             // quality at-bats (count)          GC: QAB
  qabPct?: number;          // quality at-bat rate              GC: QAB%
  hhb?: number;             // hard-hit balls (count)           GC: HHB
  contactPct?: number;      // contact rate (AB-K)/AB           GC: C%
  baRisp?: number;          // BA w/ runners in scoring pos     GC: BA/RISP
  twoOutRbi?: number;       // 2-out RBI (count)                GC: 2OUTRBI
  lob?: number;             // runners left on base (count)     GC: LOB
  ps?: number;              // pitches seen (count)             GC: PS
  psPa?: number;            // pitches seen per PA              GC: PS/PA
  bbK?: number;             // walks per strikeout              GC: BB/K
  paBb?: number;            // plate appearances per walk       GC: PA/BB
  kLooking?: number;        // strikeouts looking (count)       GC: K-L
  ldPct?: number;           // line-drive %                     GC: LD%
  fbPct?: number;           // fly-ball %                       GC: FB%
  gbPct?: number;           // ground-ball %                    GC: GB%
  babipBat?: number;        // batter BABIP                     GC: BABIP (batting section)
  twoStrikeBattle?: number; // PAs seeing 3+ pitches after 2 strikes (count)  GC: 2S+3
  battlePct?: number;       // % of PAs w/ 3+ pitches after 2 strikes         GC: 2S+3%
  // Pitching
  ip?: number;
  /** Games pitched: the pitching section's own GP column. `gp` is the batting GP. */
  gpPitching?: number;
  pitches?: number;  // total pitches thrown (#P column in GC export)
  era?: number;
  whip?: number;
  bbAllowed?: number;
  soAllowed?: number;
  hAllowed?: number;
  erAllowed?: number;
  rAllowed?: number;    // total runs allowed (pitcher) — GC pitching R column
  sPct?: number;     // strike %
  fpsPct?: number;   // first-pitch strike %
  p_bf?: number;     // pitches per batter faced
  bbInn?: number;    // walks per inning
  hrAllowed?: number;   // home runs allowed (pitcher)
  hbpAllowed?: number;  // hit batters (pitcher)
  sbAllowed?: number;   // stolen bases ALLOWED    GC pitching SB
  csAllowed?: number;   // runners caught stealing GC pitching CS
  // ── Fielding (GC fielding section, cols EZ-FN + FY) ──────────────────────
  // Captured 2026-08-27. Sixteen columns of real defensive data were parsed
  // past and discarded, which is why nothing in Dugout has an objective
  // defensive input: OVR's fielding bucket is coach opinion (glove/arm 1-5) and
  // calculateXOVR has no defensive term at all. Stored now so it accumulates;
  // how to USE it (fielding pct is weak in youth ball, and "no chances" is not
  // "good glove") is a separate design decision. See the plan appendix.
  //
  // NOTE the SB/CS split. GC reports steals twice, attributed differently:
  // pitching SB/CS is what happened while a player PITCHED; fielding SB/CS is
  // what happened while they CAUGHT. Per player they differ (Ollie: 26 pitching,
  // 17 catching); team totals match because every steal has both.
  totalChances?: number;        // GC: TC
  assists?: number;             // GC: A
  putouts?: number;             // GC: PO
  fieldingPct?: number;         // GC: FPCT
  errors?: number;              // GC: E
  doublePlays?: number;         // GC: DP
  triplePlays?: number;         // GC: TP
  inningsCaught?: number;       // GC: INN  (catcher only, thirds notation)
  passedBalls?: number;         // GC: PB
  sbAllowedCatching?: number;   // GC fielding SB — steals allowed while catching
  sbAttemptsAgainst?: number;   // GC: SBATT
  csCatching?: number;          // GC fielding CS — runners caught while catching
  csPct?: number;               // GC: CS%
  pickoffs?: number;            // GC: PIK
  catcherInterference?: number; // GC: CI
  totalInningsFielded?: number; // GC: Total (thirds notation)
  bf?: number;          // batters faced
  // Pitching — story stats (GC export, added 2026-07; absent on older imports)
  fpsoPct?: number;     // % of first-pitch-strike ABs ending in an out   GC: FPSO%
  fpswPct?: number;     // % of FPS ABs ending in a walk                  GC: FPSW%
  fpshPct?: number;     // % of FPS ABs ending in a hit                   GC: FPSH%
  zeroBbInn?: number;   // zero-walk innings (count)                      GC: 0BBINN
  bbs?: number;         // walks that scored (count)                      GC: BBS
  lobb?: number;        // leadoff walks (count)                          GC: LOBB
  loo?: number;         // leadoff outs (1st batter of inning)            GC: LOO
  inn123?: number;      // 1-2-3 innings (count)                          GC: 123INN
  lowPitchInn?: number; // innings of 13 pitches or fewer (count)         GC: <13
  smPct?: number;       // swinging-miss % of total pitches               GC: SM%
  kBf?: number;         // strikeouts per batter faced                    GC: K/BF
  weakPct?: number;     // % of batted balls weakly hit                   GC: WEAK%
  goAo?: number;        // ground-out to air-out ratio                    GC: GO/AO
  baa?: number;         // opponent batting average                       GC: BAA
  pIp?: number;         // pitches per inning                             GC: P/IP
}

export interface XOVRBreakdown {
  /** Final blended score on 1–100 scale — replaces player.ovr in Firestore */
  xovr: number;
  /** Coach's pure scouting OVR (1–100), never modified */
  scoutingOVR: number;
  /** Raw delta before confidence weighting, in OVR points (e.g. +12, −8) */
  rawStatDelta: number;
  /** Delta actually applied after confidence weighting (xovr = scoutingOVR + statDelta) */
  statDelta: number;
  /** 0.0–1.0 — fraction of the raw delta applied */
  confidenceWeight: number;
  /** Tier label */
  confidenceLevel: 'none' | 'low' | 'medium' | 'high';
  /** Human-readable sample size label e.g. "12 PA — building confidence" */
  confidenceLabel: string;
  /** Short string showing the driving signals e.g. "AVG .340 · OPS .920 · 18% K" */
  primarySignal: string;
  /** Which stat category drove the calculation */
  signalType: 'batting' | 'pitching' | 'both' | 'none';
  /** Impact breakdown for UI */
  impact?: {
    label: string;
    value: number;
  }[];
}

// ---------------------------------------------------------------------------
// Confidence weighting
// ---------------------------------------------------------------------------

/**
 * Maps plate appearances to a 0.0–1.0 confidence weight.
 * Conservative by design — youth baseball stats are noisy.
 * 30+ PA is a solid season sample at this level.
 */
export function getConfidenceWeight(pa: number): {
  weight: number;
  level: 'none' | 'low' | 'medium' | 'high';
  label: string;
} {
  if (!pa || pa <= 0) return { weight: 0,    level: 'none',   label: 'No stats yet' };
  if (pa < 6)         return { weight: 0.15, level: 'low',    label: `${pa} PA (early signal)` };
  if (pa < 12)        return { weight: 0.30, level: 'low',    label: `${pa} PA (building confidence)` };
  if (pa < 20)        return { weight: 0.60, level: 'medium', label: `${pa} PA (meaningful sample)` };
  if (pa < 30)        return { weight: 0.80, level: 'medium', label: `${pa} PA (strong sample)` };
  return               { weight: 1.00, level: 'high',   label: `${pa} PA (full weight)` };
}

export function getPitcherConfidenceWeight(ip: number): {
  weight: number;
  level: 'none' | 'low' | 'medium' | 'high';
  label: string;
} {
  if (!ip || ip < 1)  return { weight: 0,    level: 'none',   label: 'No innings yet' };
  if (ip < 3)         return { weight: 0.20, level: 'low',    label: `${ip.toFixed(1)} IP (early signal)` };
  if (ip < 6)         return { weight: 0.40, level: 'low',    label: `${ip.toFixed(1)} IP (building confidence)` };
  if (ip < 10)        return { weight: 0.65, level: 'medium', label: `${ip.toFixed(1)} IP (meaningful sample)` };
  return               { weight: 1.00, level: 'high',   label: `${ip.toFixed(1)} IP (full weight)` };
}

// ---------------------------------------------------------------------------
// OPS → raw delta  (in 1–100 OVR points)
// ---------------------------------------------------------------------------

/**
 * Dan's OPS breakpoints, in 1–100 OVR units:
 *
 *   OPS ≥ 1.100       → +15 (ceiling)
 *   OPS 1.000–1.100   → +12 to +15
 *   OPS 0.900–1.000   → +8  to +12
 *   OPS 0.800–0.900   → +5  to +8
 *   OPS 0.700–0.800   → +1  to +5
 *   OPS 0.600–0.700   → −5  to +1   ← neutral zone straddle
 *   OPS 0.500–0.600   → −7  to −5
 *   OPS 0.400–0.500   → −8  to −7
 *   OPS < 0.400        → −12 (floor)
 */
function opsDelta(ops: number): number {
  if (ops >= 1.100) return  15;
  if (ops >= 1.000) return  12 + ((ops - 1.000) / 0.100) * 3;
  if (ops >= 0.900) return   8 + ((ops - 0.900) / 0.100) * 4;
  if (ops >= 0.800) return   5 + ((ops - 0.800) / 0.100) * 3;
  if (ops >= 0.700) return   1 + ((ops - 0.700) / 0.100) * 4;
  if (ops >= 0.600) return  -5 + ((ops - 0.600) / 0.100) * 6;
  if (ops >= 0.500) return  -7 + ((ops - 0.500) / 0.100) * 2;
  if (ops >= 0.400) return  -8 + ((ops - 0.400) / 0.100) * 1;
  return -12;
}

/**
 * K% drag — reduces the OPS delta when a batter strikes out at a high rate.
 * Capped at −5 pts so it can't fully negate a strong OPS signal.
 *
 *   < 20%  →  0
 *   20–35% →  0 to −2
 *   35–50% → −2 to −4
 *   > 50%  → −5
 */
function kPctDrag(so: number, pa: number): number {
  if (!pa) return 0;
  const kPct = so / pa;
  if (kPct < 0.20) return 0;
  if (kPct < 0.35) return -((kPct - 0.20) / 0.15) * 2;
  if (kPct < 0.50) return -2 - ((kPct - 0.35) / 0.15) * 2;
  return -5;
}

// ---------------------------------------------------------------------------
// WHIP → raw delta  (in 1–100 OVR points)
// ---------------------------------------------------------------------------

/**
 * Youth baseball WHIP ranges (naturally higher than pro ball):
 *
 *   WHIP < 0.80   → +15 (dominant)
 *   WHIP 0.80–1.20 → +8 to +15
 *   WHIP 1.20–1.60 → −2 to +8
 *   WHIP 1.60–2.20 → −8 to −2
 *   WHIP > 2.20    → −12
 */
function whipDelta(whip: number): number {
  if (whip < 0.80) return  15;
  if (whip < 1.00) return   8 + ((1.00 - whip) / 0.20) * 7;
  if (whip < 1.20) return   2 + ((1.20 - whip) / 0.20) * 6;
  if (whip < 1.40) return   0 + ((1.40 - whip) / 0.20) * 2;
  if (whip < 1.60) return  -2 - ((whip - 1.40) / 0.20) * 2;
  if (whip < 2.00) return  -5 - ((whip - 1.60) / 0.40) * 3;
  if (whip < 2.50) return  -9 - ((whip - 2.00) / 0.50) * 3;
  return -12;
}

/**
 * ERA secondary modifier — max ±2 pts. Only trusted at 2+ IP.
 */
function eraModifier(era: number, ip: number): number {
  if (!ip || ip < 2) return 0;
  const clipped = Math.min(era, 15.0);
  if (clipped < 1.50) return  2;
  if (clipped < 3.00) return  1;
  if (clipped < 5.00) return  0;
  if (clipped < 8.00) return -1;
  return -2;
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Calculates xOVR for a player.
 *
 * @param scoutingOVR  Coach's attribute-derived OVR on the 1–100 scale.
 *                     Pass player.ovrBreakdown?.scoutingOVR ?? player.ovr
 *                     so that re-importing doesn't compound adjustments.
 * @param stats        Imported stats (null = no stats, return scoutingOVR unchanged)
 */
export function calculateXOVR(
  scoutingOVR: number,
  stats: PlayerStats | undefined | null,
  priorSeasonStats?: PlayerStats | null
): XOVRBreakdown {
  const base: Omit<XOVRBreakdown, 'xovr'> = {
    scoutingOVR,
    rawStatDelta:     0,
    statDelta:        0,
    confidenceWeight: 0,
    confidenceLevel:  'none',
    confidenceLabel:  'No stats yet',
    primarySignal:    '',
    signalType:       'none',
  };

  // Fallback to prior-season baseline ONLY when there's no current sample at
  // all (no PA, no IP). Once the coach plays even a single game, current
  // stats take over entirely. The prior baseline is treated like real stats
  // but capped at low confidence by getConfidenceWeight via PA — and the
  // overall stat delta is multiplied by 0.5 below so prior data can never
  // move xOVR as much as live current-season data would.
  const hasCurrentSample = stats && ((stats.pa ?? 0) > 0 || (stats.ip ?? 0) > 0);
  const usingPriorBaseline = !hasCurrentSample && !!priorSeasonStats && ((priorSeasonStats.pa ?? 0) > 0 || (priorSeasonStats.ip ?? 0) > 0);
  if (usingPriorBaseline) stats = priorSeasonStats!;

  if (!stats) return { ...base, xovr: scoutingOVR };

  // ── Batting signal ─────────────────────────────────────────────────────────
  const pa         = stats.pa ?? 0;
  const hasBatting = pa > 0 && (stats.ops ?? 0) > 0;

  let bApplied = 0, bRaw = 0, bWeight = 0;
  let bLevel: XOVRBreakdown['confidenceLevel'] = 'none';
  let bLabel = '', bSignal = '';
  let bImpact: { label: string; value: number }[] = [];

  if (hasBatting) {
    const ops         = stats.ops!;
    const so          = stats.so ?? 0;
    const opsDeltaVal = opsDelta(ops);
    const kPenaltyVal = kPctDrag(so, pa);
    bRaw              = Math.max(-10, Math.min(10, opsDeltaVal + kPenaltyVal));
    const bc          = getConfidenceWeight(pa);
    bWeight           = bc.weight;
    bLevel            = bc.level;
    bLabel            = bc.label;
    bApplied          = bRaw * bWeight;

    const avgStr = stats.avg != null ? `AVG ${stats.avg.toFixed(3).replace(/^0/, '')}` : null;
    bSignal = [avgStr, `OPS ${ops.toFixed(3).replace(/^0/, '')}`, `${Math.round((so / pa) * 100)}% K`]
      .filter(Boolean).join(' · ');
    bImpact = [
      { label: 'OPS Performance', value: Math.round(opsDeltaVal) },
      { label: 'K% Modifier',     value: Math.round(kPenaltyVal) },
    ].filter(i => i.value !== 0);
  }

  // ── Pitching signal ────────────────────────────────────────────────────────
  const ip          = stats.ip ?? 0;
  const hasPitching = ip >= 1 && (stats.whip ?? 0) > 0;

  let pApplied = 0, pRaw = 0, pWeight = 0;
  let pLevel: XOVRBreakdown['confidenceLevel'] = 'none';
  let pLabel = '', pSignal = '';
  let pImpact: { label: string; value: number }[] = [];

  if (hasPitching) {
    const whip        = stats.whip!;
    const era         = stats.era ?? 0;
    const whipDeltaVal = whipDelta(whip);
    const eraModVal   = eraModifier(era, ip);
    pRaw              = Math.max(-10, Math.min(10, whipDeltaVal + eraModVal));
    const pc          = getPitcherConfidenceWeight(ip);
    pWeight           = pc.weight;
    pLevel            = pc.level;
    pLabel            = pc.label;
    pApplied          = pRaw * pWeight;

    const eraStr = era > 0 ? ` · ERA ${era.toFixed(2)}` : '';
    pSignal = `WHIP ${whip.toFixed(2)}${eraStr} · ${ip.toFixed(1)} IP`;
    pImpact = [
      { label: 'WHIP Performance', value: Math.round(whipDeltaVal) },
      { label: 'ERA Modifier',     value: Math.round(eraModVal) },
    ].filter(i => i.value !== 0);
  }

  if (!hasBatting && !hasPitching) return { ...base, xovr: scoutingOVR };

  // ── Blend — sum both confidence-weighted signals, cap total at ±12 ─────────
  // Each signal is already scaled by its own sample-size confidence weight,
  // so a 3-PA batting sample (weight 0.15) barely moves the needle while
  // 7 IP of shutout ball (weight 0.65) carries full pull. Two-way excellence
  // is rewarded but capped at ±12 to keep the scale honest.
  // When the data comes from a prior-season baseline (no current sample at
  // all), halve the effective pull so prior data never moves xOVR as much
  // as live current-season data of the same shape would.
  const priorDecay    = usingPriorBaseline ? 0.5 : 1;
  const totalApplied  = Math.max(-12, Math.min(12, (bApplied + pApplied) * priorDecay));
  const xovr          = Math.round(Math.max(1, Math.min(100, scoutingOVR + totalApplied)));

  const hasBoth       = hasBatting && hasPitching;
  const pitchDominant = pWeight >= bWeight;

  const baseLabel = hasBoth ? (pitchDominant ? pLabel : bLabel) : (hasBatting ? bLabel : pLabel);
  return {
    xovr,
    scoutingOVR,
    rawStatDelta:     Math.round(bRaw * (hasBoth ? bWeight / (bWeight + pWeight) : 1)
                               + pRaw * (hasBoth ? pWeight / (bWeight + pWeight) : 0)),
    statDelta:        Math.round(totalApplied),
    confidenceWeight: hasBoth ? Math.max(bWeight, pWeight) : (hasBatting ? bWeight : pWeight),
    confidenceLevel:  hasBoth ? (pitchDominant ? pLevel : bLevel) : (hasBatting ? bLevel : pLevel),
    confidenceLabel:  usingPriorBaseline ? `${baseLabel} · prior season` : baseLabel,
    primarySignal:    hasBoth ? `${bSignal} · ${pSignal}` : (hasBatting ? bSignal : pSignal),
    signalType:       hasBoth ? 'both' : (hasBatting ? 'batting' : 'pitching'),
    impact:           [...bImpact, ...pImpact].filter(i => i.value !== 0),
  };
}

// ---------------------------------------------------------------------------
// CSV parsing utilities
// ---------------------------------------------------------------------------

export function findHeaderRowIndex(rows: any[][]): number {
  return rows.findIndex(row =>
    row.some(cell => String(cell ?? '').trim() === 'Number') &&
    row.some(cell => String(cell ?? '').trim() === 'Last')
  );
}

export function extractStatsFromRow(
  row: (string | number)[],
  battingMap:  Record<string, number>,
  pitchingMap: Record<string, number>,
  /** Fielding-section columns (TC..CI, Total). Optional so callers that predate
   *  the fielding capture keep working and simply get undefined fields. */
  fieldingMap: Record<string, number> = {},
): PlayerStats | null {
  const get = (col: string, map: Record<string, number>): number | null => {
    const idx = map[col];
    if (idx === undefined) return null;
    const val = row[idx];
    if (val === '' || val === '-' || val === 'N/A' || val == null) return null;
    const n = typeof val === 'number' ? val : parseFloat(String(val));
    return isNaN(n) ? null : n;
  };

  const pa = get('PA', battingMap) ?? 0;
  const ip = get('IP', pitchingMap) ?? 0;
  // GC exports total pitches as "#P" in the pitching section header
  const pitches = get('#P', pitchingMap) ?? get('P', pitchingMap) ?? 0;
  // Fielding innings count as evidence the row is a real player: a defensive
  // replacement can field without ever batting or pitching, and dropping them
  // would leave a hole in the field-time record.
  const fieldedInnings = get('Total', fieldingMap) ?? 0;
  if (pa === 0 && ip === 0 && pitches === 0 && fieldedInnings === 0) return null;

  return {
    gp:        get('GP',  battingMap),
    pa,
    ab:        get('AB',  battingMap),
    avg:       get('AVG', battingMap),
    obp:       get('OBP', battingMap),
    ops:       get('OPS', battingMap),
    slg:       get('SLG', battingMap),
    h:         get('H',   battingMap),
    bb:        get('BB',  battingMap),
    so:        get('SO',  battingMap),
    rbi:       get('RBI', battingMap),
    r:         get('R',   battingMap),
    xbh:       get('XBH', battingMap),
    sb:        get('SB',  battingMap),
    sbPct:     get('SB%', battingMap),
    hbp:       get('HBP', battingMap),
    qab:             get('QAB',     battingMap),
    qabPct:          get('QAB%',    battingMap),
    hhb:             get('HHB',     battingMap),
    contactPct:      get('C%',      battingMap),
    baRisp:          get('BA/RISP', battingMap),
    twoOutRbi:       get('2OUTRBI', battingMap) ?? get('2OUT RBI', battingMap),
    lob:             get('LOB',     battingMap),
    ps:              get('PS',      battingMap),
    psPa:            get('PS/PA',   battingMap),
    bbK:             get('BB/K',    battingMap),
    paBb:            get('PA/BB',   battingMap),
    kLooking:        get('K-L',     battingMap),
    ldPct:           get('LD%',     battingMap),
    fbPct:           get('FB%',     battingMap),
    gbPct:           get('GB%',     battingMap),
    babipBat:        get('BABIP',   battingMap),
    twoStrikeBattle: get('2S+3',    battingMap),
    battlePct:       get('2S+3%',   battingMap),
    ip,
    gpPitching: get('GP',    pitchingMap),
    pitches,
    era:       get('ERA',    pitchingMap),
    whip:      get('WHIP',   pitchingMap),
    hAllowed:  get('H',      pitchingMap),
    erAllowed: get('ER',     pitchingMap),
    rAllowed:  get('R',      pitchingMap),
    bbAllowed: get('BB',     pitchingMap),
    soAllowed: get('SO',     pitchingMap),
    sPct:      get('S%',     pitchingMap),
    fpsPct:    get('FPS%',   pitchingMap),
    p_bf:      get('P/BF',   pitchingMap),
    bbInn:     get('BB/INN', pitchingMap),
    hrAllowed:  get('HR',  pitchingMap),
    hbpAllowed: get('HBP', pitchingMap),
    // GC's pitching section carries the run game against us: "SB = Stolen bases
    // allowed", "CS = Runners caught stealing". Same values appear in the
    // fielding section (with SBATT). Read from pitching because the parser only
    // maps position columns out of the fielding section.
    sbAllowed:  get('SB',  pitchingMap),
    csAllowed:  get('CS',  pitchingMap),
    bf:         get('BF',  pitchingMap),
    fpsoPct:     get('FPSO%',  pitchingMap),
    fpswPct:     get('FPSW%',  pitchingMap),
    fpshPct:     get('FPSH%',  pitchingMap),
    zeroBbInn:   get('0BBINN', pitchingMap),
    bbs:         get('BBS',    pitchingMap),
    lobb:        get('LOBB',   pitchingMap),
    loo:         get('LOO',    pitchingMap),
    inn123:      get('123INN', pitchingMap),
    lowPitchInn: get('<13',    pitchingMap),
    smPct:       get('SM%',    pitchingMap),
    kBf:         get('K/BF',   pitchingMap),
    weakPct:     get('WEAK%',  pitchingMap),
    goAo:        get('GO/AO',  pitchingMap),
    baa:         get('BAA',    pitchingMap),
    pIp:         get('P/IP',   pitchingMap),
    // Fielding section. Absent from older imports, which is why every field is
    // optional and nothing downstream may assume a number.
    totalChances:        get('TC',    fieldingMap),
    assists:             get('A',     fieldingMap),
    putouts:             get('PO',    fieldingMap),
    fieldingPct:         get('FPCT',  fieldingMap),
    errors:              get('E',     fieldingMap),
    doublePlays:         get('DP',    fieldingMap),
    triplePlays:         get('TP',    fieldingMap),
    inningsCaught:       get('INN',   fieldingMap),
    passedBalls:         get('PB',    fieldingMap),
    sbAllowedCatching:   get('SB',    fieldingMap),
    sbAttemptsAgainst:   get('SBATT', fieldingMap),
    csCatching:          get('CS',    fieldingMap),
    csPct:               get('CS%',   fieldingMap),
    pickoffs:            get('PIK',   fieldingMap),
    catcherInterference: get('CI',    fieldingMap),
    totalInningsFielded: fieldedInnings || null,
  };
}

// ---------------------------------------------------------------------------
// Player matching
// ---------------------------------------------------------------------------

export type MatchConfidence = 'exact' | 'name' | 'number' | 'fuzzy' | 'unmatched';

export interface PlayerMatchResult {
  csvIndex:          number;
  csvLast:           string;
  csvFirst:          string;
  csvNumber:         string;
  stats:             PlayerStats | null;
  fieldTime?:        Record<string, number> | null;
  matchedPlayerId:   string | null;
  matchedPlayerName: string | null;
  confidence:        MatchConfidence;
}

function normalize(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]/g, '');
}

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
  return dp[m][n];
}

export function matchPlayers(
  csvRows: Array<{ last: string; first: string; number: string; stats: PlayerStats | null; fieldTime?: Record<string, number> | null }>,
  rosterPlayers: Array<{ id: string; name: string; number: string }>
): PlayerMatchResult[] {
  return csvRows.map((row, idx) => {
    const csvLast  = normalize(row.last);
    const csvFirst = normalize(row.first);
    const csvNum   = String(row.number).trim();

    for (const player of rosterPlayers) {
      const parts      = player.name.trim().toLowerCase().split(/\s+/);
      const pLastNorm  = normalize(parts[parts.length - 1]);
      const pFirstNorm = normalize(parts[0]);
      const playerNum  = String(player.number).trim();
      const numMatch   = csvNum === playerNum;
      const lastMatch  = csvLast === pLastNorm;
      const firstMatch = csvFirst === pFirstNorm;

      if (numMatch && lastMatch)
        return { csvIndex: idx, csvLast: row.last, csvFirst: row.first, csvNumber: row.number,
          stats: row.stats, fieldTime: row.fieldTime, matchedPlayerId: player.id, matchedPlayerName: player.name,
          confidence: 'exact' };

      if (lastMatch && firstMatch)
        return { csvIndex: idx, csvLast: row.last, csvFirst: row.first, csvNumber: row.number,
          stats: row.stats, fieldTime: row.fieldTime, matchedPlayerId: player.id, matchedPlayerName: player.name,
          confidence: 'name' };
    }

    const numOnly = rosterPlayers.find(p => String(p.number).trim() === csvNum);
    if (numOnly)
      return { csvIndex: idx, csvLast: row.last, csvFirst: row.first, csvNumber: row.number,
        stats: row.stats, fieldTime: row.fieldTime, matchedPlayerId: numOnly.id, matchedPlayerName: numOnly.name,
        confidence: 'number' };

    const fuzzy = rosterPlayers.find(p => {
      const pLast = normalize(p.name.trim().split(/\s+/).pop() ?? '');
      return levenshtein(csvLast, pLast) <= 2;
    });
    if (fuzzy)
      return { csvIndex: idx, csvLast: row.last, csvFirst: row.first, csvNumber: row.number,
        stats: row.stats, fieldTime: row.fieldTime, matchedPlayerId: fuzzy.id, matchedPlayerName: fuzzy.name,
        confidence: 'fuzzy' };

    return { csvIndex: idx, csvLast: row.last, csvFirst: row.first, csvNumber: row.number,
      stats: row.stats, fieldTime: row.fieldTime, matchedPlayerId: null, matchedPlayerName: null,
      confidence: 'unmatched' };
  });
}
